// Port of ios/Sources/ViewModels/ThreadsDerivation.swift.
import type { ThreadRecord, UserRecord } from './records';
import { messagePreview, userDisplayName } from './channelListDerivation';

/**
 * Which tab of the Threads screen is showing — the webapp's two: everything followed, or only
 * what has something new.
 */
export type ThreadsTab = 'following' | 'unreads';

/**
 * The Threads entry pinned at the top of the channel list. Both numbers come from the
 * SERVER's totals, never from summing the cached page — a badge that disagrees with the
 * desktop client is worse than no badge.
 */
export interface ThreadsEntry {
  unreadThreads: number;
  unreadMentions: number;
}

/** One row of the Threads screen, fully resolved — the view never sees a `ThreadRecord`. */
export interface DisplayThread {
  id: string;
  channelId: string;
  channelName: string;
  /** "Ana Torres", "Ana Torres y Luis Mendoza", "Ana, Luis y 3 más" — already composed. */
  participantSummary: string;
  /**
   * The thread's NEWEST reply, flattened and prefixed — falling back to the root only when
   * no reply is cached. A row saying "2 new" above the message already read answers the
   * wrong question: this line is for showing what has NOT been seen.
   */
  preview: string;
  lastReplyAt: number;
  replyCount: number;
  unreadReplies: number;
  unreadMentions: number;
  isUrgent: boolean;
  avatarUserIds: string[];
  avatarVersions: Record<string, number>;
}

function entryHasUnread(entry: ThreadsEntry): boolean {
  return entry.unreadThreads > 0 || entry.unreadMentions > 0;
}

function threadIsUnread(thread: Pick<DisplayThread, 'unreadReplies' | 'unreadMentions'>): boolean {
  return thread.unreadReplies > 0 || thread.unreadMentions > 0;
}

/**
 * Whether the global Threads view exists for this user at all. Mattermost gates collapsed
 * reply threads three ways and all three must agree: the server capability (what the threads
 * endpoint itself last answered — trusted over config, because it is the only source actually
 * TESTED; 501 = off, and nothing overrules it), the `CollapsedThreads` config, and — when the
 * config leaves it to the user — their preference. The failure mode avoided: a Threads row
 * that opens onto a screen which can only ever say "no threads". Hiding the row is the honest
 * degradation.
 */
function isEnabled(
  serverCapability: boolean | null,
  collapsedThreadsConfig: string | null,
  userPreference: string | null,
): boolean {
  if (serverCapability === false) return false;
  switch (collapsedThreadsConfig) {
    case 'disabled':
      return false;
    case 'always_on':
      return true;
    case 'default_on':
      return userPreference !== 'off';
    case 'default_off':
      return userPreference === 'on';
    // No config read yet (or a future server's unrecognised value): fall back to whether
    // the endpoint has actually worked. Before the first sync that is null, and the row
    // stays hidden until we know.
    default:
      return serverCapability === true;
  }
}

export const ThreadsAvailability = {
  isEnabled,
} as const;

// Pure logic for the global Threads view (shared/API.md §13) — the badge arithmetic, the tab
// filter and the mark-all-read transition, testable against plain values. A decision-for-
// decision port of Android's `ThreadsDerivation`.

function entry(unreadThreads: number, unreadMentions: number): ThreadsEntry {
  return { unreadThreads: Math.max(0, unreadThreads), unreadMentions: Math.max(0, unreadMentions) };
}

/**
 * What the Hilos row's badge SAYS — the number of threads waiting, which is exactly the
 * number of rows the "No leídos" tab will list.
 *
 * It used to say the MENTION count, which is the convention for a channel row and is wrong
 * here. A channel badge counts messages inside one conversation; this row leads to a LIST,
 * and a list's badge is read as "how many are in there". Reported 2026-10-05 with a
 * screenshot: badge 1, two unread threads behind it, "this doesn't add up". Both numbers
 * were correct — 2 unread threads, 1 of them mentioning you — and nothing on screen said the
 * badge was answering the other question. Mentions keep the badge's MENTION COLOUR (and each
 * row inside still carries its own mention count), so nothing is lost.
 *
 * Falls back to the mention count if the server ever reports mentions with no unread thread:
 * the two totals arrive as separate fields, and saying "1" is better than showing nothing
 * while a mention sits unread.
 */
function entryBadge(e: ThreadsEntry): string | null {
  if (e.unreadThreads > 0) return `${e.unreadThreads}`;
  if (e.unreadMentions > 0) return `${e.unreadMentions}`;
  return null;
}

/**
 * "All your threads" shows everything followed; "Unreads" only what has news — which is
 * why mark-all-read belongs on the SECOND tab: it is the only one the action visibly
 * empties.
 */
function visibleThreads(threads: readonly DisplayThread[], tab: ThreadsTab): DisplayThread[] {
  return tab === 'following' ? [...threads] : threads.filter(threadIsUnread);
}

/**
 * Mark-all-read as a pure transition over the rows currently held — the screen has to
 * empty the Unreads tab ON THE TAP, not a round trip later. Same rows, same order: the
 * FOLLOWING tab must not reshuffle just because badges cleared.
 */
function markAllRead(threads: readonly DisplayThread[]): DisplayThread[] {
  return threads.map((t) => ({ ...t, unreadReplies: 0, unreadMentions: 0 }));
}

/** The entry half of Swift's overloaded `markAllRead(_: ThreadsEntry)`. */
function markAllReadEntry(_entry: ThreadsEntry): ThreadsEntry {
  return { unreadThreads: 0, unreadMentions: 0 };
}

/**
 * Marking ONE thread read: the totals come down by exactly what that row carried — never
 * below zero, even when the totals were stale.
 */
function markRead(e: ThreadsEntry, thread: DisplayThread): ThreadsEntry {
  return {
    unreadThreads: Math.max(0, e.unreadThreads - (threadIsUnread(thread) ? 1 : 0)),
    unreadMentions: Math.max(0, e.unreadMentions - thread.unreadMentions),
  };
}

/**
 * Who is in the thread. The current user is dropped — every thread you follow has you in
 * it, so your own name on every row says nothing. Unresolved names are skipped rather than
 * shown as ids; if nothing resolves, the fallback keeps the line from being blank.
 */
function participantSummary(args: {
  participantIds: readonly string[];
  currentUserId: string;
  usersById: ReadonlyMap<string, UserRecord>;
  unknownFallback: string;
  maxNames?: number;
  andLabel: string;
  moreLabel: (count: number) => string;
}): string {
  const maxNames = args.maxNames ?? 2;
  const names: string[] = [];
  for (const id of args.participantIds) {
    if (id === args.currentUserId) continue;
    const user = args.usersById.get(id);
    if (!user) continue;
    const name = userDisplayName(user);
    if (!names.includes(name)) names.push(name);
  }
  if (names.length === 0) return args.unknownFallback;
  if (names.length === 1) return names[0];
  if (names.length <= maxNames) {
    return names.slice(0, -1).join(', ') + ' ' + args.andLabel + ' ' + names[names.length - 1];
  }
  const shown = names.slice(0, maxNames).join(', ');
  return shown + ' ' + args.andLabel + ' ' + args.moreLabel(names.length - maxNames);
}

/**
 * One line for the row's message: identical treatment to the channel list's preview, for
 * the same reasons.
 */
function rootPreview(args: {
  message: string;
  authorName: string | null;
  isOwnMessage: boolean;
  youLabel: string;
  attachmentPlaceholder: string;
}): string {
  return messagePreview({
    message: args.message,
    authorName: args.authorName,
    isOwnMessage: args.isOwnMessage,
    showsAuthorPrefix: true,
    youLabel: args.youLabel,
    attachmentPlaceholder: args.attachmentPlaceholder,
  });
}

/**
 * The stacked avatars, capped. The root author leads — the thread is their message —
 * then the participants in the server's (join) order.
 */
function avatarIds(thread: Pick<ThreadRecord, 'rootUserId'>, participantIds: readonly string[], maxCount = 3): string[] {
  const ordered: string[] = [];
  for (const id of [thread.rootUserId, ...participantIds]) {
    if (id !== '' && !ordered.includes(id)) ordered.push(id);
  }
  return ordered.slice(0, maxCount);
}

export const ThreadsDerivation = {
  entry,
  entryHasUnread,
  threadIsUnread,
  entryBadge,
  visibleThreads,
  markAllRead,
  markAllReadEntry,
  markRead,
  participantSummary,
  rootPreview,
  avatarIds,
} as const;
